import React, {useCallback, useEffect, useMemo, useReducer, useRef, useState} from "react";
import {CombatantContext, CombatantProviderReturn} from "@/encounter/combatants/CombatantHooks.ts"
import {useDebounceCallback} from "usehooks-ts";
import Combatant from "@/encounter/model/Combatant.ts";
import {
    CombatantEvent,
    CombatantSnapshot,
    addCharacterToEncounter,
    CombatantTransformBatch,
    deleteCombatants,
    getCombatants,
    saveCombatants,
    saveCombatantTransforms,
} from "@/api/CombatantApi.ts";
import {useEncounter} from "@/encounter/EncounterHooks.ts";
import type {CombatantUpdatePersistence} from "@/encounter/combatants/CombatantHooks.ts";
import {combatantReducer, createCombatantSelector, emptyCombatantState} from "./CombatantState.ts";
import {toCombatantData} from "@/encounter/model/CombatantData.ts";
import type {Character} from "@/characters/model/Character.ts";
import {useCombatantRealtime} from "@/encounter/combatants/useCombatantRealtime.ts";
import {useBackgroundErrorNotification} from "@/api/useBackgroundErrorNotification.ts";
import {useDungeon} from "@/encounter/dungeon/DungeonHooks.ts";
import {combatantsOnLevel} from "@/tabletop/tokens/CombatantLevel.ts";
import {srcUrl} from "@/api/ImageApi.ts";
import {useInitialImages} from "@/images/useInitialImages.ts";
import questionSvg from "@assets/question.svg";
import hiddenSvg from "@assets/hide.svg";
import frameShadowFile from "@assets/token-frame-shadow.webp";
import {tokenFrameImage, tokenFrameStyle} from "@/tabletop/tokens/TokenFrameStyles.ts";

const liveTransformOptions = {
    leading: true,
    trailing: true,
    maxWait: 33,
};

export function CombatantProvider({children}: { children: React.ReactNode }) {
    const encounter = useEncounter();
    const encounterId = encounter.id;
    const generation = useRef(0);
    const {report, recover} = useBackgroundErrorNotification();
    const {subscribeToUpdates, publishTransforms, onConnected} = useCombatantRealtime(encounter.id);
    const [state, dispatch] = useReducer(combatantReducer, undefined, emptyCombatantState);
    const [initialLoad, setInitialLoad] = useState<{encounterId?: string; error: boolean}>({error: false});
    const selectCombatants = useMemo(() => createCombatantSelector(), []);
    const combatants = useMemo(() => selectCombatants(state), [selectCombatants, state]);
    const dataLoading = initialLoad.encounterId !== encounter.id;
    const {dungeonSettings} = useDungeon();
    const imageUrls = useMemo(() => {
        const active = combatantsOnLevel(combatants, dungeonSettings.levelId);
        const urls = active.flatMap(combatant => [
            combatant.character.image ? srcUrl(combatant.character.image) : questionSvg,
            tokenFrameImage(tokenFrameStyle(combatant.tokenFrame)),
        ]);
        if (active.length > 0) urls.push(hiddenSvg, frameShadowFile);
        return urls;
    }, [combatants, dungeonSettings.levelId]);
    const imagesReady = useInitialImages(imageUrls, !dataLoading && !initialLoad.error);
    const initialLoading = dataLoading || !imagesReady;

    const publishLiveTransforms = useCallback((batch: CombatantTransformBatch) => {
        publishTransforms(batch);
    }, [publishTransforms]);
    const throttledPublishTransforms = useDebounceCallback(publishLiveTransforms, 33, liveTransformOptions);

    const previewTransforms = useCallback((batch: CombatantTransformBatch) => {
        dispatch({type: "event", event: {transformed: {...batch, final: false}}});
        throttledPublishTransforms(batch);
    }, [throttledPublishTransforms]);

    const commitTransforms = useCallback(async (batch: CombatantTransformBatch) => {
        throttledPublishTransforms.cancel();
        dispatch({type: "event", event: {transformed: {...batch, final: false}}});
        const requestGeneration = generation.current;
        const saved = await saveCombatantTransforms(encounter, batch);
        if (requestGeneration === generation.current) dispatch({type: "event", event: {transformed: saved}});
    }, [encounter, throttledPublishTransforms]);

    useEffect(() => {
        const effectGeneration = ++generation.current;
        dispatch({type: "reset"});

        let active = true;
        let initialSnapshotLoaded = false;
        let hasSnapshot = false;
        let loadGeneration = 0;
        const bufferedEvents: CombatantEvent[] = [];

        const unsubscribe = subscribeToUpdates(event => {
            if (!active) return;
            if (initialSnapshotLoaded) dispatch({type: "event", event});
            else bufferedEvents.push(event);
        });

        const finishInitialLoad = (snapshot: CombatantSnapshot, error = false) => {
            if (!active) return;
            dispatch({type: "replace", snapshot});
            bufferedEvents.forEach(event => dispatch({type: "event", event}));
            bufferedEvents.length = 0;
            initialSnapshotLoaded = true;
            hasSnapshot = true;
            setInitialLoad({encounterId: encounterId, error});
        };

        const refresh = () => {
            const request = ++loadGeneration;
            initialSnapshotLoaded = false;
            void getCombatants(encounterId).then(snapshot => {
                if (active && request === loadGeneration) finishInitialLoad(snapshot);
            }).catch(() => {
                if (!active || request !== loadGeneration) return;
                if (!hasSnapshot) finishInitialLoad({combatants: [], characters: []}, true);
                else {
                    bufferedEvents.forEach(event => dispatch({type: "event", event}));
                    bufferedEvents.length = 0;
                    initialSnapshotLoaded = true;
                    report(new Error("Combatants konnten nicht aktualisiert werden."), "Die Kampfteilnehmer konnten nicht aktualisiert werden.");
                }
            });
        };
        const unsubscribeConnection = onConnected(refresh);
        refresh();

        return () => {
            generation.current = effectGeneration + 1;
            active = false;
            throttledPublishTransforms.cancel();
            unsubscribe();
            unsubscribeConnection();
        };
    }, [encounterId, subscribeToUpdates, throttledPublishTransforms, onConnected, report]);

    const updateServer = useCallback(async (updated: Combatant[]) => {
        const requestGeneration = generation.current;
        const saved = await saveCombatants(encounter, updated);
        if (requestGeneration === generation.current) dispatch({type: "event", event: {upserted: saved}});
    }, [encounter]);

    const updateInBackground = useCallback(async (updated: Combatant[]) => {
        try {
            await updateServer(updated);
            recover();
        } catch (error) {
            report(error, "Die Token-Änderungen konnten nicht gespeichert werden.");
        }
    }, [recover, report, updateServer]);
    const debouncedUpdate = useDebounceCallback(updateInBackground, 100);

    useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate]);

    const update = async (
        updated: Combatant[],
        persistence: CombatantUpdatePersistence = "immediate",
    ) => {
        if (persistence === "confirmed") {
            debouncedUpdate.cancel();
            await updateServer(updated);
            return;
        }
        dispatch({type: "event", event: {upserted: updated.map(toCombatantData)}});

        if (persistence === "debounced") {
            debouncedUpdate(updated);
        } else {
            debouncedUpdate.cancel();
            await updateServer(updated);
        }
    }

    const longRest = async () => await update(combatants
        .filter(combatant => !combatant.hostile)
        .map(combatant => ({
            ...combatant,
            currentLife: combatant.maxLife,
            conditions: []
        }))
    );

    const remove = async (remove: Combatant[]) => {
        const requestGeneration = generation.current;
        const removedIds = await deleteCombatants(encounter, remove);
        if (requestGeneration === generation.current) dispatch({type: "event", event: {removedIds}});
    }

    const addCharacter = async (character: Character) => {
        const requestGeneration = generation.current;
        const saved = await addCharacterToEncounter(encounter.id, character.id);
        if (requestGeneration === generation.current) dispatch({type: "event", event: saved});
    };


    const providerReturn: CombatantProviderReturn = {
        combatants,
        initialLoading,
        update,
        remove,
        addCharacter,
        longRest,
        previewTransforms,
        commitTransforms,
    }

    if (initialLoad.error) throw new Error("Combatants konnten nicht geladen werden.");

    return (
        <CombatantContext.Provider value={providerReturn}>
            {children}
        </CombatantContext.Provider>
    );

}
